import {
  designReviewReport,
  type DesignReviewReport as Report,
  type ReportFinding,
} from '../design-review-report.js';
import type { RecordFactView } from '../api.js';
import { humanise } from '../register-finding.js';

/**
 * The design review as canon's report reads (A3 S9): the short version, the items to settle first,
 * then every other finding under its discipline, each once. The same view for staff and a client; the
 * API decides what each was sent, and the full register stays on the Record tab for staff.
 */

/** "1 item", "3 items". */
const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The short version's first sentence: how many, then where they stand. Zero parts are left out. */
export function standingSentence(c: Report['counts']): string {
  const parts = [
    c.open > 0 ? `${c.open} ${c.open === 1 ? 'is' : 'are'} open` : null,
    c.answered > 0 ? `${c.answered} ${c.answered === 1 ? 'is' : 'are'} answered or closed` : null,
    c.retired > 0
      ? `${c.retired} ${c.retired === 1 ? 'was' : 'were'} withdrawn or replaced by a later item`
      : null,
    c.noStatus > 0
      ? `${c.noStatus} ${c.noStatus === 1 ? 'has' : 'have'} no status we can show`
      : null,
  ].filter((p): p is string => p !== null);
  const head = `${count(c.shown, 'item', 'items')} from the design review ${c.shown === 1 ? 'is' : 'are'} on this record.`;
  if (parts.length === 0) return head;
  const tail =
    parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
  return `${head} ${tail?.charAt(0).toUpperCase()}${tail?.slice(1)}.`;
}

function FindingLine({ f, group }: { f: ReportFinding; group: string | null }) {
  // The register's own discipline text, unless it is the heading it sits under.
  const discipline =
    f.discipline !== null && f.discipline.toLowerCase() !== group?.toLowerCase()
      ? f.discipline
      : null;
  return (
    <li className="drr__item">
      <p className="finding__summary">{f.summary}</p>
      <p className="finding__chips">
        {f.status !== null && (
          <span className="finding__chip finding__chip--status" title={f.status}>
            {humanise(f.status)}
          </span>
        )}
        {discipline !== null && <span className="finding__discipline">{discipline}</span>}
        {f.whoActs !== null && <span className="muted">Who acts: {f.whoActs}</span>}
        {/* The item's id, last, so a reader can name it when they answer it. */}
        <span className="muted">{f.key}</span>
      </p>
      {/* Shown, never hidden: a person decides what it means (Brad, 2026-10-02). */}
      {f.sourceWithdrawn && (
        <p className="card__note" role="note">
          Every document this item was raised against has since been withdrawn.
        </p>
      )}
    </li>
  );
}

export function DesignReviewReport({
  facts,
  withheld,
}: {
  facts: readonly RecordFactView[];
  withheld: number | null;
}) {
  const report = designReviewReport(facts, withheld);
  const c = report.counts;
  const held = c.withheld ?? 0;

  return (
    <section className="card drr">
      <h3 className="card__title">Design review</h3>

      <h4>The short version</h4>
      {c.shown === 0 ? (
        <p>
          {held > 0
            ? `The ${count(held, 'design-review item', 'design-review items')} on this record ${held === 1 ? 'is' : 'are'} not shown here.`
            : 'No design-review items are on this record.'}
        </p>
      ) : (
        <>
          <p>{standingSentence(c)}</p>
          {c.settleFirst > 0 && (
            <p>
              {c.settleFirst === 1
                ? '1 must be settled before the work can be priced. It is listed first.'
                : `${c.settleFirst} must be settled before the work can be priced. They are listed first.`}
            </p>
          )}
          {held > 0 && (
            <p>
              {held === 1
                ? '1 more item is not shown here.'
                : `${held} more items are not shown here.`}
            </p>
          )}
        </>
      )}

      {report.settleFirst.length > 0 && (
        <>
          <h4>Settle these first</h4>
          <ul className="drr__list">
            {report.settleFirst.map((f) => (
              // No heading names a discipline here, so every line shows its own.
              <FindingLine key={f.key} f={f} group={null} />
            ))}
          </ul>
        </>
      )}

      {report.byDiscipline.length > 0 && (
        <>
          <h4>The findings, by discipline</h4>
          {report.byDiscipline.map((g) => (
            <div key={g.discipline} className="drr__group">
              <h5>
                {g.discipline} <span className="muted">({g.findings.length})</span>
              </h5>
              <ul className="drr__list">
                {g.findings.map((f) => (
                  <FindingLine key={f.key} f={f} group={g.discipline} />
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

/** Is there anything for the tab to show? Findings, or a count of ones held back. */
export function hasDesignReview(facts: readonly RecordFactView[], withheld: number | null) {
  return (withheld ?? 0) > 0 || designReviewReport(facts, withheld).counts.shown > 0;
}
